<template>
  <div
    v-if="ready"
    class="setup_step card"
  >
    <base-modal
      v-if="showPreviewModal"
      @close="showPreviewModal = false;"
    >
      <template #heading>
        Preview your registration page
      </template>
      <iframe
        style="height: 66vh;"
        src="/merchant/registration-preview"
        width="100%"
      />
    </base-modal>
    <h4>Set up your checkout page</h4>
    <p>
      It is now time to create a custom checkout page where users can sign up for your products. Please complete the
      provided fields in order to start selling!
    </p>

    <!-- TODO: Provide recommended dimensions -->
    <label>Logo</label>
    <input
      id="logo-upload"
      ref="logo"
      class="form-control logo-upload"
      type="file"
      accept="image/*"
      @change="setRegistrationField({logo: $refs.logo.files[0]})"
    >
    <label
      v-if="!logoSrc"
      class="logo-upload-label"
      for="logo-upload"
    >
      <svg-vue icon="icons/media" />
      Add Logo
    </label>
    <div
      v-else
      class="logo-preview"
    >
      <div
        class="clear-logo"
        @click="clearFile"
      >
        <svg-vue icon="icons/x" />
      </div>

      <img :src="logoSrc">
    </div>
    <div
      v-if="!logoSrc"
      class="logo-requirements"
    >
      <p>This is a logo image to help customers easily identify your business.</p>
      <p>Recommended Size – 250 x 250 pixels (minimum 120 x 120 pixels; maximum 5200 x 5300 pixels)</p>
      <p>*If no logo is selected, your white-label logo will be used by default.</p>
    </div>

    <label>Page heading</label>
    <input
      class="form-control"
      type="text"
      :value="registration.title"
      @change="setRegistrationField({ title: $event.target.value })"
    >
    <small
      v-if="errors.title"
      class="text-danger"
      v-html="errors.title"
    />

    <!-- TODO: HIKE-627, should probably re-implement this on the merchant registration page
    <label>Copy below heading</label>
    <textarea
      class="form-control"
      :value="registration.subtitle"
      @change="setRegistrationField({ subtitle: $event.target.value })"
    />
    <small
      v-if="errors.subtitle"
      class="text-danger"
      v-html="errors.subtitle"
    />
    -->

    <button
      class="button info"
      @click="preview"
    >
      Preview
    </button>
    <button
      class="button"
      :disabled="loading"
      @click="complete"
    >
      <i
        v-if="loading"
        class="fas fa-spin fa-spinner"
      />
      Continue
    </button>
  </div>
</template>

<script>
import Joi from 'joi';
import _ from 'lodash';
import { mapActions, mapGetters } from "vuex";
import axios from 'axios';
import BaseModal from "../../global/modals/BaseModal";

export default {
  components: {
    BaseModal,
  },
  data() {
    return {
      showPreviewModal: false,
      ready: false,
      errors: {},
    };
  },
  computed: {
    ...mapGetters('merchantCentreSetupWizard', [
      'registration',
      'whitelabelLogo',
      'loading',
    ]),
    logoSrc() {
      const isFile = this.registration.logo !== null && typeof this.registration?.logo === 'object';
      const s3Path = !isFile && this.registration?.logo
        ? 'https://s3.eu-west-2.amazonaws.com/seo-public/'+this.registration?.logo
        : null;
      
      return isFile
          ? URL.createObjectURL(this.registration?.logo)
          : s3Path;
    },
  },
  mounted() {
    if (this.whitelabelLogo) {
      this.setRegistrationField({logo: this.whitelabelLogo});
    }
    setTimeout(() => { // TODO: Last minute hacky solution to solve bad scroll position HIKE-623
      this.ready = true;
    }, 50);
  },
  methods: {
    ...mapActions('merchantCentreSetupWizard', [
      'setRegistrationField',
      'setCurrentStep',
      'setLoadingState',
    ]),
    clearFile() {
      this.logoFile = null;
      this.$refs.logo.value = null;
      this.setRegistrationField({logo: null});
    },
    validateForm() {
      const stringSchema = Joi.string().min(3).max(255).required();

      const registrationSchema = Joi.object({
        title: stringSchema.label('Page title'),
        subtitle: stringSchema.label('Subtitle'),
      });

      this.errors = {};
      const { title, subtitle } = this.registration;
      const { error } = registrationSchema.validate({ title, subtitle }, { abortEarly: false });

      if (error) {
        _.forEach(error.details, error => {
          this.errors[error.context.key] = `${error.message}.`;
        });
        return false;
      }

      return true;
    },
    preview() {
      if (this.validateForm()) {
        this.saveForm().then(() => {
          this.showPreviewModal = true;
        });
      }
    },
    complete() {
      this.setLoadingState(true);
      if (!this.validateForm()) {
        this.setLoadingState(false);
        return;
      }

      this.saveForm().then(() => {
        axios.post('/merchant/complete-setup')
          .then(() => {
            this.setCurrentStep('READY_TO_GO');
          })
          .finally(() => this.setLoadingState(false));
      });
    },
    saveForm() {
      return new Promise((resolve, reject) => {
        const { title, subtitle, logo } = this.registration;
        const formData = new FormData();
        formData.append('title', title);
        formData.append('subtitle', subtitle);
        if (logo) {
          formData.append('logo', logo);
        }

        axios.post('/merchant/store-registration-info', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(() => {
          resolve();
        }).catch(e => {
          reject(e);
        });
      });
    }
  },

}
</script>

<style lang="scss" scoped>
.merchant_centre_wizard {
  .setup_form {
    .setup_step {
      > h4 {
        margin: 0;
      }

      label {
        font-weight: 500;
        font-size: 1.125rem;
        margin: 0;
      }

      .form-control {
        margin: 0.5rem 0 1.5rem 0;
      }

      .logo-upload {
        width: 0.1px;
        height: 0.1px;
        opacity: 0;
        overflow: hidden;
        position: absolute;
        z-index: -1;
        
        &+label * {
          pointer-events: none;
        }

        &+label {
          cursor: pointer;
          box-sizing: border-box;
          width: 120px;
          height: 120px;
          background: #DFF5EE;
          border: 1px solid #37B2A8;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #37B2A8;
          font-weight: 400;
          font-size: 14px;
          line-height: 18px;
          letter-spacing: -0.5px;
          margin: 8px 0;

          svg {
            width: 24px;
            height: 24px;

            :deep(path) {
              stroke: #37B2A8;
            }
          }
        }

      }

      .logo-preview {
        width: 120px;
        height: 120px;
        margin: 8px 0;
        border-radius: 12px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .clear-logo {
          cursor: pointer;
          position: absolute;
          top: 5px;
          right: 5px;
          height: 16px;
          width: 16px;
          border-radius: 16px;
          background-color: red;
          display: flex;
          align-items: center;
          justify-content: center;

          svg {
            height: 10px;
            width: 10px;

            :deep(path) {
              stroke: #FFFFFF;
            }
          }
        }

        img {
          max-height: 100%;
          max-width: 100%;
        }
      }

      .logo-requirements {
        margin-bottom: 1.5rem;

        p {
          font-style: normal;
          font-weight: 400;
          font-size: 12px;
          line-height: 16px;
          letter-spacing: -0.1px;
          color: #4E4E54;
          text-align: left;
          margin-bottom: .25rem;

          &:last-of-type {
            font-weight: 700;
          }
        }
      }
    }
  }
}
.merchant_centre_wizard {
  .setup_form {
    .setup_step {
      .button {
        &.info {
          border-radius: 12px;
          padding: 1rem;
          font-size: 1.5rem;
          line-height: 2.125rem;
          font-weight: 700;
          width: 100%;
          color: #4E4E54;
          border: 2px solid #E8E5E2;
        }
      }
    }
  }
}
</style>
